import React, { useState, useEffect, useMemo } from 'react';
import { AddHandlingTierProps } from './types';
import { registerAddedTier, isTierAdded, getAddedTiers, logTierRegistry, TIER_ADDED_EVENT, getRegistryVersion } from './tierRegistry';
import { getAllTierValues } from 'src/types/WarehouseHandlingTier';

/**
 * Compact inline component for adding new handling tiers to a Production Capability
 * This version is designed to be rendered inline within a table cell, not as a separate row
 */
export const InlineAddHandlingTier: React.FC<AddHandlingTierProps> = ({
    pcId,
    availableTiers,
    onAdd,
    userCanEdit,
    initialAdding = false
}) => {
    const [isAdding, setIsAdding] = useState(initialAdding);
    const [selectedTier, setSelectedTier] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    
    // Track when we need to force re-renders
    const [registryUpdateCounter, setRegistryUpdateCounter] = useState(0);
    
    // Listen for registry update events
    useEffect(() => {
        const handleTierAdded = (event: Event) => {
            // Force re-render when any tier is added
            setRegistryUpdateCounter(prev => prev + 1);
        };
        
        // Add event listener
        window.addEventListener(TIER_ADDED_EVENT, handleTierAdded);
        
        // Clean up
        return () => {
            window.removeEventListener(TIER_ADDED_EVENT, handleTierAdded);
        };
    }, [pcId]); // Only re-add listener if pcId changes
    
    // Initialize our understanding of tiers that have been added to this PC
    useEffect(() => {
        if (pcId) {
            const standardTiers = getAllTierValues();
            const missingTiers = standardTiers.filter(tier => !availableTiers.includes(tier));
            
            // Register any tiers that are missing from availableTiers with the tierRegistry
            missingTiers.forEach(tierName => {
                if (!isTierAdded(pcId, tierName)) {
                    registerAddedTier(pcId, tierName);
                }
            });
        }
    }, [pcId, availableTiers]);

    // Create filtered tier options outside of the render JSX and before any conditional returns
    const filteredTierOptions = useMemo(() => {
        // Filter the dropdown options
        const filteredTiers = availableTiers.filter(tier => !isTierAdded(pcId, tier));
        
        // Return the option elements
        return filteredTiers.map(tier => (
            <option key={tier} value={tier}>{tier}</option>
        ));
    }, [availableTiers, pcId, registryUpdateCounter]);

    // Handle the Add button click
    const handleAddClick = React.useCallback(() => {
        // Make sure we're capturing the correct selectedTier at this moment
        const tierToAdd = selectedTier;
        
        // Exit if no tier selected
        if (!tierToAdd || !pcId) {
            return;
        }
        
        // Prevent duplicate adds by checking the tierRegistry first
        // before we register it (not after)
        if (isTierAdded(pcId, tierToAdd)) {
            setIsAdding(false);
            setSelectedTier('');
            return;
        }

        // Set processing state to prevent multiple clicks
        setIsProcessing(true);
        
        // Call the add handler FIRST
        onAdd(pcId, tierToAdd);
        
        // Then register the tier AFTER onAdd is called
        try {
            setTimeout(() => {
                registerAddedTier(pcId, tierToAdd);
            }, 50);
        } catch (e) {
            // Silent error handling in production
        }
        
        // Reset UI state
        setIsAdding(false);
        setSelectedTier('');
        
        // Reset processing state after a short delay
        setTimeout(() => {
            setIsProcessing(false);
        }, 300);
    }, [pcId, selectedTier, onAdd]);

    // Handle cancel click
    const handleCancelClick = React.useCallback(() => {
        setIsAdding(false);
        setSelectedTier('');
    }, []);

    // Early return if user can't edit
    if (!userCanEdit) {
        return null;
    }

    // If we're not in adding mode, display the button to start adding
    if (!isAdding) {
        return (
            <button 
                className="btn btn-add btn-sm"
                onClick={() => setIsAdding(true)}
                disabled={availableTiers.length === 0}
                title={availableTiers.length === 0 ? "No available tiers to add" : "Add Warehouse Handling Tier"}
            >
                <span className="add-icon">+</span> Add Warehouse Handling Tier
            </button>
        );
    }

    // Display the tier selection form when in adding mode
    return (
        <div className="inline-add-tier-form">
            <select
                className="tier-select-inline"
                value={selectedTier}
                onChange={(e) => setSelectedTier(e.target.value)}
            >
                <option value="">-- Select Tier --</option>
                {filteredTierOptions}
            </select>
            <button
                className="add-button save-button"
                disabled={!selectedTier || isProcessing}
                onClick={handleAddClick}
            >
                {isProcessing ? 'Adding...' : 'Add'}
            </button>
            <button
                className="cancel-button"
                onClick={handleCancelClick}
            >
                Cancel
            </button>
        </div>
    );
};
